import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../App";
import { Layout } from "../components/Layout";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Textarea } from "../components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import { toast } from "sonner";
import {
  Plus,
  Search,
  MoreHorizontal,
  Eye,
  Edit,
  Phone,
  Mail,
  Building2,
  Briefcase
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const MembersPage = () => {
  const { user, chapter } = useAuth();
  const navigate = useNavigate();
  const [members, setMembers] = useState([]);
  const [powerTeams, setPowerTeams] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingMember, setEditingMember] = useState(null);
  
  // Filters
  const [searchQuery, setSearchQuery] = useState("");
  const [filterPT, setFilterPT] = useState("");
  const [filterCategory, setFilterCategory] = useState("");

  const token = localStorage.getItem("token");
  const primaryColor = chapter?.primary_color || "#CC0000";
  const canEdit = user?.role === "admin";

  const [formData, setFormData] = useState({
    full_name: "",
    company: "",
    mobile: "",
    email: "",
    power_team_id: "",
    category_id: "",
    role: "member",
    status: "active"
  });

  useEffect(() => {
    fetchData();
  }, [filterPT, filterCategory, searchQuery]);

  const fetchData = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      
      let url = `${API}/members?`;
      if (filterPT) url += `power_team_id=${filterPT}&`;
      if (filterCategory) url += `category_id=${filterCategory}&`;
      if (searchQuery) url += `search=${searchQuery}&`;

      const [membersRes, ptRes, catRes] = await Promise.all([
        fetch(url, { headers }),
        fetch(`${API}/power-teams`, { headers }),
        fetch(`${API}/categories`, { headers })
      ]);

      if (membersRes.ok) setMembers(await membersRes.json());
      if (ptRes.ok) setPowerTeams(await ptRes.json());
      if (catRes.ok) setCategories(await catRes.json());
    } catch (error) {
      console.error("Fetch error:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const url = editingMember 
        ? `${API}/members/${editingMember.member_id}`
        : `${API}/members`;
      
      const response = await fetch(url, {
        method: editingMember ? "PUT" : "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        toast.success(editingMember ? "Member updated" : "Member added");
        setDialogOpen(false);
        resetForm();
        fetchData();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Operation failed");
      }
    } catch (error) {
      toast.error("Error saving member");
    }
  };

  const resetForm = () => {
    setFormData({
      full_name: "",
      company: "",
      mobile: "",
      email: "",
      power_team_id: "",
      category_id: "",
      role: "member",
      status: "active"
    });
    setEditingMember(null);
  };

  const openEditDialog = (member) => {
    setEditingMember(member);
    setFormData({
      full_name: member.full_name || "",
      company: member.company || "",
      mobile: member.mobile || "",
      email: member.email || "",
      power_team_id: member.power_team_id || "",
      category_id: member.category_id || "",
      role: member.role || "member",
      status: member.status || "active"
    });
    setDialogOpen(true);
  };

  const getPowerTeamName = (id) => powerTeams.find(pt => pt.power_team_id === id)?.name || "-";
  const getCategoryName = (id) => categories.find(c => c.category_id === id)?.name || "-";

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6" data-testid="members-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-3xl font-display font-bold text-slate-900">Member Directory</h1>
            <p className="text-muted-foreground mt-1">{members.length} members</p>
          </div>
          
          {canEdit && (
            <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
              <DialogTrigger asChild>
                <Button style={{ backgroundColor: primaryColor }} className="hover:opacity-90" data-testid="add-member-btn">
                  <Plus className="mr-2 h-4 w-4" />
                  Add Member
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-lg">
                <DialogHeader>
                  <DialogTitle>{editingMember ? "Edit Member" : "Add New Member"}</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="full_name">Full Name *</Label>
                      <Input
                        id="full_name"
                        value={formData.full_name}
                        onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="company">Company</Label>
                      <Input
                        id="company"
                        value={formData.company}
                        onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="mobile">Mobile</Label>
                      <Input
                        id="mobile"
                        value={formData.mobile}
                        onChange={(e) => setFormData({ ...formData, mobile: e.target.value })}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="email">Email</Label>
                      <Input
                        id="email"
                        type="email"
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Power Team</Label>
                      <Select value={formData.power_team_id} onValueChange={(v) => setFormData({ ...formData, power_team_id: v })}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select" />
                        </SelectTrigger>
                        <SelectContent>
                          {powerTeams.filter(pt => pt.status === "active").map(pt => (
                            <SelectItem key={pt.power_team_id} value={pt.power_team_id}>{pt.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Category</Label>
                      <Select value={formData.category_id} onValueChange={(v) => setFormData({ ...formData, category_id: v })}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select" />
                        </SelectTrigger>
                        <SelectContent>
                          {categories.filter(c => c.status === "active").map(cat => (
                            <SelectItem key={cat.category_id} value={cat.category_id}>{cat.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Role</Label>
                      <Select value={formData.role} onValueChange={(v) => setFormData({ ...formData, role: v })}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="member">Member</SelectItem>
                          <SelectItem value="host">Visitor Host</SelectItem>
                          <SelectItem value="admin">Admin</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Status</Label>
                      <Select value={formData.status} onValueChange={(v) => setFormData({ ...formData, status: v })}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="active">Active</SelectItem>
                          <SelectItem value="inactive">Inactive</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="flex justify-end gap-3 pt-4">
                    <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                      Cancel
                    </Button>
                    <Button type="submit" style={{ backgroundColor: primaryColor }}>
                      {editingMember ? "Update" : "Add"} Member
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          )}
        </div>

        {/* Filters */}
        <Card className="border-0 shadow-sm">
          <CardContent className="pt-6">
            <div className="flex flex-wrap gap-4">
              <div className="flex-1 min-w-[200px]">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Search members..."
                    className="pl-10"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    data-testid="member-search-input"
                  />
                </div>
              </div>
              
              <Select value={filterPT || "all"} onValueChange={(v) => setFilterPT(v === "all" ? "" : v)}>
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder="Power Team" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Power Teams</SelectItem>
                  {powerTeams.map(pt => (
                    <SelectItem key={pt.power_team_id} value={pt.power_team_id}>{pt.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select value={filterCategory || "all"} onValueChange={(v) => setFilterCategory(v === "all" ? "" : v)}>
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder="Category" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Categories</SelectItem>
                  {categories.map(cat => (
                    <SelectItem key={cat.category_id} value={cat.category_id}>{cat.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Members Grid */}
        {members.length === 0 ? (
          <Card className="border-0 shadow-sm">
            <CardContent className="py-12 text-center text-muted-foreground">
              No members found. Add your first member to get started.
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="members-grid">
            {members.map((member, index) => (
              <Card 
                key={member.member_id} 
                className="border-0 shadow-sm hover:shadow-md transition-shadow cursor-pointer animate-fade-in"
                style={{ animationDelay: `${index * 0.03}s` }}
                onClick={() => navigate(`/members/${member.member_id}`)}
                data-testid={`member-card-${member.member_id}`}
              >
                <CardContent className="pt-6">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      <div 
                        className="w-12 h-12 rounded-full flex items-center justify-center text-white font-bold text-lg"
                        style={{ backgroundColor: primaryColor }}
                      >
                        {member.full_name?.charAt(0)?.toUpperCase()}
                      </div>
                      <div>
                        <h3 className="font-semibold text-slate-900">{member.full_name}</h3>
                        <p className="text-sm text-muted-foreground">{member.company || "No company"}</p>
                      </div>
                    </div>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                        <Button variant="ghost" size="icon">
                          <MoreHorizontal className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={(e) => { e.stopPropagation(); navigate(`/members/${member.member_id}`); }}>
                          <Eye className="mr-2 h-4 w-4" />
                          View Profile
                        </DropdownMenuItem>
                        {canEdit && (
                          <DropdownMenuItem onClick={(e) => { e.stopPropagation(); openEditDialog(member); }}>
                            <Edit className="mr-2 h-4 w-4" />
                            Edit
                          </DropdownMenuItem>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>

                  <div className="mt-4 space-y-2">
                    <div className="flex items-center gap-2 text-sm">
                      <Briefcase className="h-4 w-4 text-muted-foreground" />
                      <span className="text-slate-600">{getCategoryName(member.category_id)}</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm">
                      <Building2 className="h-4 w-4 text-muted-foreground" />
                      <span className="text-slate-600">{getPowerTeamName(member.power_team_id)}</span>
                    </div>
                  </div>

                  {(member.profile?.services_offered || member.profile?.current_ask) && (
                    <div className="mt-4 p-3 bg-slate-50 rounded-lg">
                      {member.profile?.services_offered && (
                        <div className="mb-2">
                          <p className="text-xs text-muted-foreground mb-1">Services Offered</p>
                          <p className="text-sm text-slate-700 line-clamp-2">{member.profile.services_offered}</p>
                        </div>
                      )}
                      {member.profile?.current_ask && (
                        <div>
                          <p className="text-xs text-muted-foreground mb-1">Current Ask</p>
                          <p className="text-sm text-slate-700 line-clamp-2">{member.profile.current_ask}</p>
                        </div>
                      )}
                    </div>
                  )}

                  <div className="mt-4 flex items-center justify-between text-sm">
                    <Badge variant="outline" className="capitalize">{member.role}</Badge>
                    <Badge className={member.status === "active" ? "bg-green-100 text-green-700" : "bg-slate-100 text-slate-700"}>
                      {member.status}
                    </Badge>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
};

export default MembersPage;
